import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import { Link } from "react-router-dom";
import { 
  Users, 
  Hammer, 
  HardHat, 
  Wrench, 
  Ruler, 
  Shield, 
  Building, 
  Phone, 
  Mail, 
  MapPin, 
  DollarSign, 
  Calendar,
  Trophy,
  GraduationCap,
  CheckCircle
} from "lucide-react";

const Careers = () => {
  const jobCategories = [
    {
      title: "Skilled Trades",
      icon: <Hammer className="h-6 w-6" />,
      description: "Licensed professionals in specialized construction trades",
      positions: [
        {
          title: "Licensed Electrician",
          type: "Full-time",
          location: "GTA & Surrounding Areas",
          requirements: ["309A Electrician License", "3+ years experience", "Valid G license"],
          salary: "$70,000 - $95,000"
        },
        {
          title: "Red Seal Plumber",
          type: "Full-time", 
          location: "Ontario-wide",
          requirements: ["Red Seal Certification", "5+ years experience", "Emergency service availability"],
          salary: "$65,000 - $90,000"
        },
        {
          title: "Journeyman Carpenter",
          type: "Full-time",
          location: "Hamilton & Surrounding",
          requirements: ["Journeyman Certificate", "Framing & finishing experience", "Own tools"],
          salary: "$60,000 - $80,000"
        },
        {
          title: "HVAC Technician",
          type: "Full-time",
          location: "Toronto Region",
          requirements: ["G2 Gas License", "HVAC certification", "Commercial experience preferred"],
          salary: "$55,000 - $85,000"
        },
        {
          title: "Certified Welder",
          type: "Contract/Full-time",
          location: "Project-based",
          requirements: ["CWB certification", "Structural welding experience", "Mobile welding equipment"],
          salary: "$28 - $45/hour"
        }
      ]
    },
    {
      title: "Construction Laborers",
      icon: <HardHat className="h-6 w-6" />,
      description: "Essential workforce for construction and site preparation",
      positions: [
        {
          title: "General Construction Laborer",
          type: "Full-time",
          location: "Multiple Sites",
          requirements: ["Construction experience preferred", "Physical fitness", "Safety training"],
          salary: "$18 - $25/hour"
        },
        {
          title: "Site Safety Officer",
          type: "Full-time",
          location: "Ontario-wide",
          requirements: ["NCSO certification", "3+ years safety experience", "Strong communication skills"],
          salary: "$55,000 - $75,000"
        },
        {
          title: "Heavy Equipment Operator",
          type: "Seasonal/Full-time",
          location: "GTA Projects",
          requirements: ["Valid equipment licenses", "5+ years operating experience", "Clean driving record"],
          salary: "$25 - $35/hour"
        }
      ]
    },
    {
      title: "General Contractors & Project Management",
      icon: <Building className="h-6 w-6" />,
      description: "Leadership roles overseeing construction projects",
      positions: [
        {
          title: "Project Manager",
          type: "Full-time",
          location: "Toronto Office",
          requirements: ["PMP or equivalent", "5+ years construction PM experience", "Degree in Construction Management"],
          salary: "$75,000 - $110,000"
        },
        {
          title: "Site Supervisor",
          type: "Full-time",
          location: "Field-based",
          requirements: ["Supervisory experience", "Construction background", "Leadership skills"],
          salary: "$60,000 - $85,000"
        },
        {
          title: "Estimator",
          type: "Full-time",
          location: "Head Office",
          requirements: ["Construction estimating software", "5+ years experience", "Strong analytical skills"],
          salary: "$65,000 - $90,000"
        }
      ]
    },
    {
      title: "Engineers & Planners",
      icon: <Ruler className="h-6 w-6" />,
      description: "Professional engineers and project planners",
      positions: [
        {
          title: "Structural Engineer (P.Eng)",
          type: "Full-time/Contract",
          location: "Office & Field",
          requirements: ["P.Eng designation", "Structural design experience", "AutoCAD proficiency"],
          salary: "$80,000 - $120,000"
        },
        {
          title: "Construction Planner",
          type: "Full-time",
          location: "Remote/Office",
          requirements: ["Project planning software", "Construction scheduling", "Risk assessment skills"],
          salary: "$60,000 - $85,000"
        }
      ]
    },
    {
      title: "Tarion Licensed Builders",
      icon: <Shield className="h-6 w-6" />,
      description: "Licensed home builders for residential construction",
      positions: [
        {
          title: "Tarion Builder/Contractor",
          type: "Full-time",
          location: "Residential Projects",
          requirements: ["Valid Tarion license", "Home building experience", "Customer service focus"],
          salary: "$70,000 - $100,000"
        },
        {
          title: "Renovation Specialist",
          type: "Full-time",
          location: "GTA Residential",
          requirements: ["Renovation experience", "Customer relations", "Quality craftsmanship"],
          salary: "$50,000 - $75,000"
        }
      ]
    }
  ];

  const benefits = [
    {
      icon: <DollarSign className="h-5 w-5" />,
      title: "Competitive Compensation",
      description: "Market-leading salaries with performance bonuses and overtime opportunities"
    },
    {
      icon: <Shield className="h-5 w-5" />,
      title: "Comprehensive Benefits",
      description: "Health, dental, vision, life insurance, and RRSP matching programs"
    },
    {
      icon: <GraduationCap className="h-5 w-5" />,
      title: "Training & Development",
      description: "Ongoing skills training, certification support, and career advancement opportunities"
    },
    {
      icon: <Calendar className="h-5 w-5" />,
      title: "Work-Life Balance",
      description: "Flexible scheduling, paid time off, and family-friendly policies"
    },
    {
      icon: <Trophy className="h-5 w-5" />,
      title: "Recognition Programs",
      description: "Employee of the month, safety awards, and milestone recognition"
    },
    {
      icon: <Wrench className="h-5 w-5" />,
      title: "Tool & Equipment Support",
      description: "Company tools, equipment allowances, and vehicle programs for qualified positions"
    }
  ];

  const companyValues = [
    "Safety First - We prioritize the safety of our team above all else",
    "Quality Craftsmanship - We take pride in delivering exceptional work",
    "Integrity - We conduct business with honesty and transparency", 
    "Teamwork - We support each other to achieve common goals",
    "Continuous Learning - We invest in our people's growth and development",
    "Client Focus - We build lasting relationships through excellent service"
  ];

  const applicationProcess = [
    {
      step: "1",
      title: "Submit Application",
      description: "Send your resume and cover letter for positions that interest you"
    },
    {
      step: "2", 
      title: "Initial Review",
      description: "Our HR team reviews applications within 3-5 business days"
    },
    {
      step: "3",
      title: "Phone/Video Interview", 
      description: "Initial interview to discuss your background and the role"
    },
    {
      step: "4",
      title: "Skills Assessment",
      description: "Practical skills test or portfolio review depending on the position"
    },
    {
      step: "5",
      title: "Final Interview",
      description: "Meet with the hiring manager and team members"
    },
    {
      step: "6",
      title: "Background Check",
      description: "Reference checks, criminal background check, and drug screening"
    },
    {
      step: "7",
      title: "Job Offer",
      description: "Receive your offer letter with compensation and start date details"
    }
  ];

  const jobBoardSchema = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "hiringOrganization": {
      "@type": "Organization",
      "name": "Blue Knight Contracting and Construction",
      "sameAs": "https://blueknightcontracting.ca"
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Toronto",
        "addressRegion": "Ontario",
        "addressCountry": "CA"
      }
    },
    "industry": "Construction",
    "employmentType": "FULL_TIME"
  };

  return (
    <div className="min-h-screen bg-background">
      <SchemaMarkup type="LocalBusiness" data={{}} />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-glow text-primary-foreground py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            Join Our Team - Construction Careers in Ontario
          </h1>
          <p className="text-xl opacity-90 max-w-3xl mx-auto mb-8">
            Build your career with Blue Knight Contracting. We're hiring skilled trades professionals, 
            laborers, contractors, engineers, and Tarion builders across Ontario.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="hero" size="lg" className="bg-primary-foreground text-white hover:bg-primary-foreground/90">
              <Mail className="mr-2 h-5 w-5" />
              View Open Positions
            </Button>
            <Button variant="outline" size="lg" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
              <Phone className="mr-2 h-5 w-5" />
              Call HR: (647) 760-8854
            </Button>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Why Choose Blue Knight Contracting?
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Join Ontario's leading construction company and build a rewarding career 
              with competitive benefits, ongoing training, and advancement opportunities.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-2 hover:border-primary/20 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 p-3 rounded-lg shrink-0">
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">{benefit.title}</h3>
                      <p className="text-muted-foreground">{benefit.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Current Job Openings
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Explore career opportunities across skilled trades, construction management, 
              engineering, and specialized contracting roles.
            </p>
          </div>

          <div className="space-y-12">
            {jobCategories.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                <div className="flex items-center gap-4 mb-8">
                  <div className="bg-primary p-3 rounded-lg text-primary-foreground">
                    {category.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">{category.title}</h3>
                    <p className="text-muted-foreground">{category.description}</p>
                  </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                  {category.positions.map((position, positionIndex) => (
                    <Card key={positionIndex} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">{position.title}</CardTitle>
                            <CardDescription className="mt-2">
                              <div className="flex items-center gap-2 mb-1">
                                <MapPin className="h-4 w-4" />
                                {position.location}
                              </div>
                              <div className="flex items-center gap-2">
                                <DollarSign className="h-4 w-4" />
                                {position.salary}
                              </div>
                            </CardDescription>
                          </div>
                          <Badge variant="secondary">{position.type}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="mb-4">
                          <h4 className="font-semibold mb-2">Requirements:</h4>
                          <ul className="space-y-1">
                            {position.requirements.map((req, reqIndex) => (
                              <li key={reqIndex} className="flex items-center gap-2 text-sm">
                                <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                                {req}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <Button className="w-full">
                          Apply for {position.title}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Our Values & Culture
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                At Blue Knight Contracting, we believe in creating a workplace where 
                every team member can thrive, grow, and contribute to our shared success.
              </p>
              <div className="space-y-4">
                {companyValues.map((value, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <p className="text-muted-foreground">{value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-primary/5 p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-6">Employee Testimonial</h3>
              <blockquote className="text-lg italic text-muted-foreground mb-4">
                "I've been with Blue Knight for over 5 years, starting as a laborer and working my way up to site supervisor. 
                The company truly invests in their people - they supported my training, encouraged my growth, and 
                recognized my contributions. This is more than just a job; it's a career."
              </blockquote>
              <footer className="font-semibold">
                - Mike Thompson, Site Supervisor
              </footer>
            </div>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Our Hiring Process
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              We've streamlined our hiring process to be efficient and thorough, 
              ensuring the right fit for both you and our team.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {applicationProcess.slice(0, 4).map((step, index) => (
              <Card key={index} className="text-center">
                <CardContent className="pt-6">
                  <div className="bg-primary text-primary-foreground w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  <h3 className="font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-6">
            {applicationProcess.slice(4).map((step, index) => (
              <Card key={index} className="text-center">
                <CardContent className="pt-6">
                  <div className="bg-primary text-primary-foreground w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  <h3 className="font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Get answers to common questions about working at Blue Knight Contracting.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible>
              <AccordionItem value="item-1">
                <AccordionTrigger>What qualifications do I need for skilled trades positions?</AccordionTrigger>
                <AccordionContent>
                  Requirements vary by trade, but typically include relevant certifications (Red Seal, provincial licenses), 
                  2-5 years of experience, and own tools for many positions. We also value safety training and clean driving records.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2">
                <AccordionTrigger>Do you hire apprentices or entry-level workers?</AccordionTrigger>
                <AccordionContent>
                  Yes! We actively support apprenticeship programs and hire motivated individuals looking to start their construction careers. 
                  We provide mentorship, training opportunities, and support for obtaining certifications.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3">
                <AccordionTrigger>What areas of Ontario do you serve?</AccordionTrigger>
                <AccordionContent>
                  Blue Knight Contracting serves the entire province of Ontario, with major operations in the GTA, Hamilton, 
                  Ottawa, London, Kitchener-Waterloo, and surrounding regions. Some positions may require travel between job sites.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4">
                <AccordionTrigger>What safety training do you provide?</AccordionTrigger>
                <AccordionContent>
                  We provide comprehensive safety training including WHMIS, fall protection, confined space entry, and equipment-specific training. 
                  All employees receive ongoing safety education and we maintain strict safety protocols on all job sites.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-5">
                <AccordionTrigger>How do I apply for multiple positions?</AccordionTrigger>
                <AccordionContent>
                  You can apply for multiple positions by submitting separate applications for each role. 
                  In your cover letter, please indicate which positions interest you most and why you'd be a good fit for each.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-6">
                <AccordionTrigger>Do you offer career advancement opportunities?</AccordionTrigger>
                <AccordionContent>
                  Absolutely. We prioritize promoting from within and offer clear career progression paths. 
                  Many of our supervisors and managers started in entry-level positions and advanced through training, 
                  mentorship, and demonstrated performance.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Build Your Future?
          </h2>
          <p className="text-xl opacity-90 max-w-3xl mx-auto mb-8">
            Join Ontario's premier construction team. Submit your application today or contact our HR department 
            to learn more about career opportunities at Blue Knight Contracting.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button variant="hero" size="lg" className="bg-primary-foreground text-white hover:bg-primary-foreground/90">
                <Mail className="mr-2 h-5 w-5" />
                Submit Application
              </Button>
            </Link>
            <Button variant="outline" size="lg" className="text-primary border-white">
              <Phone className="mr-2 h-5 w-5" />
              Call HR Department
            </Button>
          </div>
          
          <div className="mt-12 bg-primary-foreground/10 p-6 rounded-lg max-w-2xl mx-auto">
            <h3 className="text-xl font-semibold mb-4">Send Your Resume</h3>
            <div className="space-y-2 text-sm opacity-90">
              <p><strong>Email:</strong> careers@blueknightcontracting.ca</p>
              <p><strong>Phone:</strong> (647) 760-8854</p>
              <p><strong>Address:</strong> 123 Construction Way, Toronto, ON M5V 3A8</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Careers;